import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Navigate, Link } from "react-router-dom";
import { formatAmount } from "@/lib/currency";
import { Truck, Package as PackageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

const PACKAGE_LABEL = { pdf: "PDF", cad: "CAD", revit: "Revit" };
const DELIVERY_STATUS_LABEL = {
  pending: "Delivery pending",
  sent: "Files sent",
  completed: "Delivered",
  n_a: "",
};

const Purchases = () => {
  const { user, authLoading } = useApp();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/purchases").then((r) => setPurchases(r.data)).catch(() => setPurchases([])).finally(() => setLoading(false));
  }, [user]);

  if (authLoading) return <Layout><div className="p-24 text-center">Loading…</div></Layout>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-14 lg:py-20">
        <div className="overline text-accent">Library</div>
        <h1 className="font-display text-5xl lg:text-6xl mt-3 mb-12 leading-none tracking-tighter">My plans</h1>

        {loading && <div className="text-muted-foreground">Loading…</div>}
        {!loading && purchases.length === 0 && (
          <div className="text-center py-16 border border-border">
            <p className="text-muted-foreground">You haven&apos;t purchased any plans yet.</p>
            <Link to="/plans"><Button className="rounded-none bg-primary mt-6 h-12 px-8" data-testid="browse-from-empty">Browse plans</Button></Link>
          </div>
        )}
        {!loading && purchases.map((p) => {
          const manualNeeded = !!p.manual_delivery_required;
          // Build a plan-id → tier lookup so each plan row shows its package.
          const tierByPlan = {};
          (p.line_items || []).forEach((li) => { tierByPlan[li.plan_id] = li.package_tier || "pdf"; });
          return (
          <div key={p.purchase_id} className="border border-border p-6 mb-6" data-testid={`purchase-${p.purchase_id}`}>
            <div className="flex flex-wrap justify-between items-baseline gap-3">
              <div>
                <div className="overline text-accent">Purchased {new Date(p.created_at).toLocaleDateString()}</div>
                <div className="font-display text-3xl mt-2">{formatAmount(p.amount, p.currency)}</div>
              </div>
              <div className="text-sm text-muted-foreground">{p.plans?.length} plan(s)</div>
            </div>
            {manualNeeded && (
              <div className="mt-4 border border-accent/40 bg-accent/5 p-3 flex items-start gap-2" data-testid={`purchase-manual-notice-${p.purchase_id}`}>
                <Truck size={14} className="text-accent shrink-0 mt-0.5" />
                <div className="text-xs text-muted-foreground leading-relaxed">
                  {p.delivery_status === "completed" ? (
                    <><span className="font-medium text-foreground">Delivered.</span> Your CAD/Revit files have been emailed. Contact us if you need them re-sent.</>
                  ) : p.delivery_status === "sent" ? (
                    <><span className="font-medium text-foreground">Files sent.</span> Check your inbox for the CAD/Revit delivery from our team.</>
                  ) : (
                    <><span className="font-medium text-foreground">Delivery in progress.</span> Our team is preparing your CAD/Revit files and will email them within our committed timeframe.</>
                  )}
                </div>
              </div>
            )}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {p.plans?.map((plan) => {
                const tier = tierByPlan[plan.id] || "pdf";
                return (
                <div key={plan.id} className="flex gap-4 border border-border p-4" data-testid={`purchase-plan-${plan.id}`}>
                  <div className="w-20 h-20 shrink-0 bg-muted overflow-hidden"><img src={plan.images?.[0]} alt="" className="w-full h-full object-cover" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl">{plan.title}</div>
                    <div className="text-xs text-muted-foreground">{plan.style} · {plan.sqft?.toLocaleString?.()} sqft</div>
                    <div className="mt-2 inline-flex items-center gap-1 border border-primary/40 bg-primary/5 px-2 py-0.5" data-testid={`purchase-tier-${plan.id}`}>
                      <PackageIcon size={10} className="text-primary" />
                      <span className="text-[10px] uppercase tracking-widest text-primary font-medium">{PACKAGE_LABEL[tier] || tier} Package</span>
                    </div>
                    {plan.has_pdf && tier === "pdf" && (
                      <div className="mt-2 text-xs text-muted-foreground">PDF delivery is automatic — check your email or contact support if not received.</div>
                    )}
                    {tier !== "pdf" && DELIVERY_STATUS_LABEL[p.delivery_status || "pending"] && (
                      <div className="mt-2 text-[11px] uppercase tracking-widest text-accent">{DELIVERY_STATUS_LABEL[p.delivery_status || "pending"]}</div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>
    </Layout>
  );
};

export default Purchases;
